/**
 * Brand UI kit for the rule builder — Tailwind primitives matching
 * CreateSchemeWizard (blue-600 primary, rounded-xl cards, circular stepper,
 * indigo chips). Keeps the builder visually consistent with the scheme flow.
 */
import React from "react";
import { HiCheck } from "react-icons/hi";
import { FiAlertCircle, FiInfo } from "react-icons/fi";

export const cx = (...c) => c.filter(Boolean).join(" ");

export function Btn({ variant = "primary", className, ...p }) {
  const base = "inline-flex items-center justify-center gap-1.5 rounded-lg text-sm font-semibold transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98]";
  const styles = {
    primary: "px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow",
    ghost: "px-5 py-2.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    danger: "px-4 py-2 border border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
    soft: "px-3.5 py-1.5 bg-blue-50/80 text-blue-700 hover:bg-blue-100/80 font-medium border border-blue-100/60",
  };
  return <button className={cx(base, styles[variant], className)} {...p} />;
}

export function Card({ className, active, children, ...p }) {
  return (
    <div
      className={cx(
        "rounded-xl border transition-all duration-200 bg-white",
        active
          ? "border-blue-500 ring-4 ring-blue-500/10 shadow-md bg-gradient-to-b from-blue-50/20 to-white"
          : "border-gray-200 hover:border-gray-300 shadow-xs",
        className
      )}
      {...p}
    >
      {children}
    </div>
  );
}

export function Chip({ color = "indigo", children, className }) {
  const c = {
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200/80",
    blue: "bg-blue-50 text-blue-700 border-blue-200/80",
    gray: "bg-gray-100 text-gray-700 border-gray-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
  }[color] || "bg-gray-100 text-gray-700 border-gray-200";

  return (
    <span className={cx("inline-flex items-center gap-1 rounded-full text-xs font-semibold px-2.5 py-0.5 border shadow-2xs", c, className)}>
      {children}
    </span>
  );
}

export function Field({ label, error, hint, children, className }) {
  return (
    <div className={className}>
      {label && <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">{label}</label>}
      {children}
      {error && (
        <p className="mt-1.5 text-xs text-rose-600 font-medium flex items-center gap-1">
          <FiAlertCircle className="shrink-0 text-rose-500" />
          <span>{error}</span>
        </p>
      )}
      {!error && hint && (
        <p className="mt-1 text-xs text-gray-500 flex items-center gap-1">
          <FiInfo className="shrink-0 text-gray-400" />
          <span>{hint}</span>
        </p>
      )}
    </div>
  );
}

const fieldBase =
  "w-full rounded-lg border bg-white px-3.5 py-1 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all shadow-2xs";

export function TextInput({ error, className, ...p }) {
  return (
    <input
      className={cx(
        fieldBase,
        error ? "border-rose-300 focus:ring-rose-500/30 focus:border-rose-500 bg-rose-50/20" : "border-gray-300",
        className
      )}
      {...p}
    />
  );
}

export function Select({ error, options = [], placeholder, value, onChange, className, ...p }) {
  return (
    <select
      className={cx(
        fieldBase,
        "appearance-none pr-8 bg-no-repeat bg-[right_0.75rem_center]",
        error ? "border-rose-300 focus:ring-rose-500/30 focus:border-rose-500 bg-rose-50/20" : "border-gray-300",
        className
      )}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      {...p}
    >
      {placeholder && <option value="" disabled>{placeholder}</option>}
      {options.map((o) =>
        o.group ? (
          <optgroup key={o.group} label={o.group}>
            {o.options.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
          </optgroup>
        ) : (
          <option key={o.value} value={o.value}>{o.label}</option>
        )
      )}
    </select>
  );
}

/** Segmented control (ALL/ANY, reward modes). options: [{value,label}] */
export function Segmented({ options, value, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200/80 bg-gray-100/70 p-1 shadow-inner">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cx(
            "px-3.5 py-1.5 text-xs font-semibold rounded-md transition-all duration-150 select-none",
            value === o.value
              ? "bg-blue-600 text-white shadow-sm"
              : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Horizontal circular stepper. steps: [{label}], current index, hasError(i)->bool */
export function StepBar({ steps, current, onGo, hasError }) {
  return (
    <div className="flex items-center w-full">
      {steps.map((s, i) => {
        const active = i === current;
        const done = i < current;
        const err = hasError?.(i);
        return (
          <React.Fragment key={s.label}>
            <button
              type="button"
              onClick={() => onGo(i)}
              className="flex flex-col items-center gap-1.5 focus:outline-none group shrink-0 transition-transform active:scale-95"
            >
              <span
                className={cx(
                  "flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold transition-all shadow-xs",
                  err
                    ? "bg-rose-600 text-white ring-4 ring-rose-100"
                    : active
                    ? "bg-blue-600 text-white ring-4 ring-blue-100 shadow-blue-200"
                    : done
                    ? "bg-emerald-600 text-white"
                    : "bg-gray-100 text-gray-500 border border-gray-300 group-hover:border-gray-400 group-hover:bg-gray-200"
                )}
              >
                {done && !err ? <HiCheck className="h-5 w-5" /> : i + 1}
              </span>
              <span
                className={cx(
                  "text-xs font-medium transition-colors",
                  active
                    ? "font-bold text-blue-700"
                    : err
                    ? "font-semibold text-rose-600"
                    : done
                    ? "text-gray-700"
                    : "text-gray-400 group-hover:text-gray-600"
                )}
              >
                {s.label}
              </span>
            </button>
            {i < steps.length - 1 && (
              <div
                className={cx(
                  "flex-1 h-1 mx-2 md:mx-4 rounded-full transition-all duration-300",
                  done ? "bg-emerald-500" : i === current ? "bg-gradient-to-r from-blue-600 to-gray-200" : "bg-gray-200"
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function IconBtn({ children, className, ...p }) {
  return (
    <button
      type="button"
      className={cx(
        "p-1.5 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors focus:outline-none",
        className
      )}
      {...p}
    >
      {children}
    </button>
  );
}

